import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// graphql
import { PLAN_KPI_LIST_QUERY } from 'graphql/planKPIs/queries'
import { FILTER_SIDEBAR_QUERY } from 'graphql/filter/queries'
import { PLAN_KPI_CREATE_MUTATION } from 'graphql/planKPIs/mutations'
import { PLAN_ITEM_CREATE_MUTATION } from 'graphql/plan/mutations'
// types
import { ConfigItem } from 'layouts/types'
import { TRefetch } from 'types/refetch'
import { MutationFn } from 'types/apollo'
import { NCreatePlanItem } from 'types/plan/helpers'
import { NCreatePlanKPI, NGetPlanKPI } from 'types/kpis/helpers'
import { FilterTarget, IField, IPlan, IPlanKpi } from 'types/graphql'
// utils
import get from 'lodash/get'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import getFp from 'lodash/fp/get'
import { safeGetOr } from 'utils/fp'
import { getFieldsList } from 'utils/fields'
import { getSidebarConfig } from 'utils/layouts'

type Props = React.PropsWithChildren<unknown>

type TKpiCreate = MutationFn<NCreatePlanKPI.IResponse, NCreatePlanKPI.IVariables>
type TPlanItemCreate = MutationFn<NCreatePlanItem.IResponse, NCreatePlanItem.IVariables>

interface Context {
  data: IPlanKpi[]
  fields: IField[]
  config: ConfigItem[]
  refetch: TRefetch<NGetPlanKPI.IResponse, NGetPlanKPI.IVariables>
  isLoading: boolean
  kpiCreate: TKpiCreate
  totalCount: number
  pageFilter: string
  showKPIModal: (id: string, plan: IPlan) => any
  isRefetching: boolean
  planItemCreate: TPlanItemCreate
}

export const KPIPageContext = React.createContext<Context>({} as any)

const KPIPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const querySlug = router.query.slug?.[0]

  const { search, filter, page, perPage } = React.useContext(TableManagementContext)

  const { data: filtersQuery, loading } = useQuery(FILTER_SIDEBAR_QUERY, {
    variables: {
      filter: {
        target: FilterTarget.Kpi
      }
    }
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const { data, networkStatus, refetch } = useQuery(PLAN_KPI_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        description: search,
        filterSlug: querySlug
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const [kpiCreate] = useMutation(PLAN_KPI_CREATE_MUTATION)
  const [planItemCreate] = useMutation(PLAN_ITEM_CREATE_MUTATION)

  // constants
  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.KPI)
  const filters = safeGetOr<any, any, any[]>([], 'filters.data')(filtersQuery)
  const config = getSidebarConfig(filters, 'kpis')

  const pageFilter = React.useMemo(() => {
    if (querySlug) {
      return pipe(
        find(({ slug }) => querySlug === slug),
        getFp('name')
      )(filters)
    }
  }, [filters, querySlug])

  const kpis: IPlanKpi[] = get(data, 'planKPIs.data', [])
  const totalCount: number = get(data, 'planKPIs.pagination.totalCount', 0)

  React.useEffect(() => {
    if (filters.length && !pageFilter) {
      router.push(`/kpis/${filters[0].slug}`)
    }
  }, [filters, pageFilter])

  const value = {
    config,
    fields,
    refetch,
    kpiCreate,
    pageFilter,
    totalCount,
    planItemCreate,
    data: kpis,
    isLoading: loading || networkStatus === 1,
    isRefetching: networkStatus === 2
  } as Context

  return <KPIPageContext.Provider value={value}>{children}</KPIPageContext.Provider>
}

export default KPIPageAPI
